import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { 
  FiTruck, FiCalendar, FiDollarSign, 
  FiTrendingUp, FiPlus, FiArrowRight, FiClock,
  FiSettings, FiLock
} from 'react-icons/fi';
import { ManagerLayout } from '../../components/layout';
import { Card, Loading } from '../../components/common';
import { useAuth } from '../../context/AuthContext';
import { getCarStats, getCarsByCompany } from '../../firebase/cars';
import { getBookingStats, getBookingsByCompany } from '../../firebase/bookings';

const StatCard = ({ title, value, icon: Icon, color, subtext, link }) => (
  <Card className="p-6">
    <div className="flex items-start justify-between">
      <div>
        <p className="text-sm font-medium text-gray-500">{title}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
        {subtext && <p className="text-sm text-gray-500 mt-1">{subtext}</p>}
      </div>
      <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${color}`}>
        <Icon size={24} className="text-white" />
      </div>
    </div>
    {link && (
      <Link 
        to={link} 
        className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 mt-4 font-medium"
      >
        View all <FiArrowRight size={14} />
      </Link>
    )}
  </Card>
);

const ManagerDashboard = () => {
  const { managerCompanyId } = useAuth();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    cars: { total: 0, active: 0 },
    bookings: { total: 0, totalRevenue: 0, upcoming: 0 }
  });
  const [recentCars, setRecentCars] = useState([]);
  const [recentBookings, setRecentBookings] = useState([]);

  useEffect(() => {
    if (managerCompanyId) {
      loadDashboardData();
    }
  }, [managerCompanyId]);

  const loadDashboardData = async () => {
    if (!managerCompanyId) return;
    
    try {
      const [carStats, bookingStats, cars, bookings] = await Promise.all([
        getCarStats(managerCompanyId),
        getBookingStats(managerCompanyId),
        getCarsByCompany(managerCompanyId, { includeInactive: true }),
        getBookingsByCompany(managerCompanyId)
      ]);

      setStats({
        cars: carStats.stats || { total: 0, active: 0 },
        bookings: bookingStats.stats || { total: 0, totalRevenue: 0, upcoming: 0 }
      });

      setRecentCars(cars.cars?.slice(0, 5) || []);
      setRecentBookings(bookings.bookings?.slice(0, 5) || []);

    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <ManagerLayout title="Dashboard">
        <div className="flex items-center justify-center h-64">
          <Loading />
        </div>
      </ManagerLayout>
    );
  }

  return (
    <ManagerLayout title="Dashboard">
      {/* Welcome Section */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900">Welcome back!</h2>
        <p className="text-gray-600 mt-1">Here's what's happening with your company today.</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Cars"
          value={stats.cars.total}
          icon={FiTruck}
          color="bg-blue-500"
          subtext={`${stats.cars.active} available`}
          link="/manager/cars"
        />
        <StatCard
          title="Total Bookings"
          value={stats.bookings.total}
          icon={FiCalendar}
          color="bg-purple-500"
          subtext={`${stats.bookings.upcoming} upcoming`}
          link="/manager/bookings"
        />
        <StatCard
          title="Revenue"
          value={`₹${stats.bookings.totalRevenue?.toLocaleString() || 0}`}
          icon={FiDollarSign}
          color="bg-green-500"
          subtext="All time"
        />
        <StatCard
          title="Upcoming"
          value={stats.bookings.upcoming || 0}
          icon={FiTrendingUp}
          color="bg-amber-500"
          subtext="Upcoming bookings"
        />
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Link to="/manager/cars/new">
          <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer border-2 border-dashed border-gray-200 hover:border-primary-300">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-primary-100 rounded-lg flex items-center justify-center">
                <FiPlus size={20} className="text-primary-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">Add New Car</h3>
                <p className="text-xs text-gray-500">List a new vehicle</p>
              </div>
            </div>
          </Card>
        </Link>
        
        <Link to="/manager/bookings">
          <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer border-2 border-dashed border-gray-200 hover:border-primary-300">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                <FiCalendar size={20} className="text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">Manage Bookings</h3>
                <p className="text-xs text-gray-500">View all reservations</p>
              </div>
            </div>
          </Card>
        </Link>
        
        <Link to="/manager/cars">
          <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer border-2 border-dashed border-gray-200 hover:border-primary-300">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <FiLock size={20} className="text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">Block Cars</h3>
                <p className="text-xs text-gray-500">Block dates/times</p>
              </div>
            </div>
          </Card>
        </Link>
        
        <Link to="/manager/settings">
          <Card className="p-4 hover:shadow-md transition-shadow cursor-pointer border-2 border-dashed border-gray-200 hover:border-primary-300">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <FiSettings size={20} className="text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">Company Settings</h3>
                <p className="text-xs text-gray-500">Edit company details</p>
              </div>
            </div>
          </Card>
        </Link>
      </div>

      {/* Recent Data */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Cars */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Your Cars</h3>
            <Link to="/manager/cars" className="text-sm text-primary-600 hover:text-primary-700">
              View all
            </Link>
          </div>
          {recentCars.length > 0 ? (
            <div className="space-y-3">
              {recentCars.map((car) => (
                <Link 
                  key={car.id} 
                  to={`/manager/cars/${car.id}`}
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                    {car.images?.[0] ? (
                      <img src={car.images[0]} alt={car.name} className="w-full h-full object-contain bg-gray-100" />
                    ) : (
                      <FiTruck className="text-gray-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">{car.name}</p>
                    <p className="text-sm text-gray-500">₹{car.price12Hour}/12hr • ₹{car.price24Hour}/24hr</p>
                  </div>
                  <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                    car.status === 'active' 
                      ? 'bg-green-100 text-green-700' 
                      : car.status === 'maintenance'
                      ? 'bg-yellow-100 text-yellow-700'
                      : 'bg-gray-100 text-gray-700'
                  }`}>
                    {car.status}
                  </span>
                </Link>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FiTruck size={32} className="mx-auto mb-2 opacity-50" />
              <p>No cars added yet</p>
              <Link 
                to="/manager/cars/new" 
                className="text-primary-600 hover:text-primary-700 text-sm"
              >
                Add your first car
              </Link>
            </div>
          )}
        </Card>

        {/* Recent Bookings */}
        <Card className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Recent Bookings</h3>
            <Link to="/manager/bookings" className="text-sm text-primary-600 hover:text-primary-700">
              View all
            </Link>
          </div>
          {recentBookings.length > 0 ? (
            <div className="space-y-3">
              {recentBookings.map((booking) => (
                <div 
                  key={booking.id} 
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  <div className="w-12 h-12 bg-gray-200 rounded-lg flex items-center justify-center overflow-hidden">
                    {booking.carDetails?.image ? (
                      <img src={booking.carDetails.image} alt={booking.carDetails.name} className="w-full h-full object-contain bg-gray-100" />
                    ) : (
                      <FiTruck className="text-gray-500" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-gray-900 truncate">
                      {booking.carDetails?.name || 'Unknown Car'}
                    </p>
                    <p className="text-sm text-gray-500">
                      {booking.customerDetails?.name || booking.customerDetails?.email || 'Unknown Customer'}
                    </p>
                  </div>
                  <div className="text-right">
                    <p className="font-medium text-gray-900">₹{booking.totalAmount?.toLocaleString()}</p>
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      booking.status === 'confirmed' 
                        ? 'bg-green-100 text-green-700' 
                        : booking.status === 'cancelled'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-gray-100 text-gray-700'
                    }`}>
                      {booking.status}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-gray-500">
              <FiCalendar size={32} className="mx-auto mb-2 opacity-50" />
              <p>No bookings yet</p>
            </div>
          )}
        </Card>
      </div>
    </ManagerLayout>
  );
};

export default ManagerDashboard;
